// Markdown → HTML with LaTeX preservation. Ported from the upstream v1 app.js
// (v1 formatLatex) verbatim — that version handles images, tables, escaped
// subscripts, and the LaTeX-inside-HTML interaction correctly; the earlier v2
// rewrite was lossy (no images, broken paragraph wrapping).

// A table whose header row is all empty cells (`| | | |`) is a HEADERLESS GRID —
// a magic square, a fill-in board, a printed number figure — where every row is
// data. GFM has no headerless table, so the empty header row is the convention
// the book pipeline types for it; it renders as <tbody> only (no shaded <th> row
// claiming the grid's first line is a label) and gets the compact grid style.
//
// The delimiter row must stay on ONE line and carry a dash: a `\s` there once let
// it run on through the newline and swallow an all-empty first body row, so a
// grid whose top line is blank (a magic square with only two cells filled)
// silently lost that row.
function processMarkdownTables(text) {
    const tableRegex = /(\|.+\|[\r\n]+)(\|[-: \t|]*-[-: \t|]*\|[ \t]*[\r\n]+)((?:\|.+\|[\r\n]*)+)/g;
    return text.replace(tableRegex, (_match, header, _sep, body) => {
        const splitRow = (row) => {
            const cells = row.trim().split('|');
            if (cells.length && cells[0].trim() === '') cells.shift();
            if (cells.length && cells[cells.length - 1].trim() === '') cells.pop();
            return cells.map(c => c.trim());
        };
        const headerCells = splitRow(header);
        const bodyRows = body.trim().split('\n').map(splitRow).filter(r => r.length > 0);
        const colCount = headerCells.length;
        const pad = (row) => { while (row.length < colCount) row.push(''); return row; };
        const grid = headerCells.every(c => c === '');
        let html = `<table class="markdown-table${grid ? ' markdown-grid' : ''}">`;
        if (!grid) {
            html += '<thead><tr>';
            pad(headerCells).forEach(c => { html += `<th>${c}</th>`; });
            html += '</tr></thead>';
        }
        html += '<tbody>';
        bodyRows.forEach(row => {
            html += '<tr>';
            pad(row).forEach(c => { html += `<td>${c}</td>`; });
            html += '</tr>';
        });
        html += '</tbody></table>';
        return '\n' + html + '\n';
    });
}

// Build-time image dimensions ({ "<filename>": [w,h] }), lazily supplied by
// data.js::loadImageDims → setImageDims once an image-bearing view renders. Lets
// formatLatex stamp width/height on figures so the browser reserves their space
// before load (no cumulative layout shift). Null until loaded / in Node builds,
// where figures simply render without dimensions (as before).
let imageDims = null;
export function setImageDims(map) { imageDims = map || null; }

// width/height attrs for a figure, so `max-width:100%;height:auto` scales it
// responsively while the intrinsic ratio reserves its box. Local images come
// from the imageDims sidecar; remote figures (e.g. Mathpix) carry ?width/?height
// in the URL, so read those directly — no sidecar entry needed.
function imgDimsAttr(src) {
    const w = src.match(/[?&]width=(\d+)/i), hh = src.match(/[?&]height=(\d+)/i);
    if (w && hh) return ` width="${w[1]}" height="${hh[1]}"`;
    if (imageDims) {
        const name = src.replace(/^.*?images\//, '').replace(/[?#].*$/, '');
        const d = imageDims[name];
        if (d && d[0] && d[1]) return ` width="${d[0]}" height="${d[1]}"`;
    }
    return '';
}

// Bullet lists. The book agents and translators type a case split as lines
// opening «- » (or «* », «• ») right under a sentence, with no blank line, and
// this renderer had no lists: the cases printed as raw hyphens run together by
// <br>s, and a «* » bullet could even pair with the next one's star into an
// italic span (3,700+ such lines in published solutions and statements,
// 2026-10-05). A run of such lines becomes one <ul class="md-list">; a line
// indented 2+ spaces under an item continues it. A lone «* » line is left
// alone (a footnote mark, not a list). Numbered lines («1) », «а) ») stay text:
// printed as the source numbered them, they already read as a list.
const LIST_ITEM_RE = /^[ \t]{0,3}([-*•])[ \t]+(\S.*)$/;
function processMarkdownLists(text) {
    const lines = text.split('\n');
    const out = [];
    for (let i = 0; i < lines.length; i++) {
        const m = LIST_ITEM_RE.exec(lines[i]);
        if (!m) { out.push(lines[i]); continue; }
        const items = [m[2]];
        let j = i + 1;
        for (; j < lines.length; j++) {
            const next = LIST_ITEM_RE.exec(lines[j]);
            if (next) items.push(next[2]);
            else if (/^[ \t]{2,}\S/.test(lines[j])) items[items.length - 1] += '\n' + lines[j].trim();
            else break;
        }
        if (m[1] === '*' && items.length < 2) { out.push(lines[i]); continue; }
        // Blank lines either side make it a block of its own in the paragraph
        // pass below; formatLatex then lifts it out of its <p>.
        out.push(`\n<ul class="md-list">${items.map(x => `<li>${x}</li>`).join('')}</ul>\n`);
        i = j - 1;
    }
    // At either end of the text there is no paragraph to set it apart from, and
    // a lone newline there would become a <br>.
    return out.join('\n').replace(/^\n+(?=<ul class="md-list">)/, '').replace(/(<\/ul>)\n+$/, '$1');
}

// `opts.lists` — render bullet lists (above). Opt-in for now: the problem page
// asks for it; the worksheet (and its Word export), the result cards and the
// proxy's server-rendered statement still get the plain text they were built on.
export function formatLatex(text, opts = {}) {
    if (!text) return '';
    let html = text;

    // Stash $$…$$ / \[…\] (display math) and $…$ / \(…\) (inline math) so
    // the markdown step doesn't chew up their contents. \[…\] and \(…\) are
    // KaTeX delimiters too (KATEX_DELIMITERS), but they used to go through the
    // markdown pass: a multi-line \[ \begin{cases} … \] got <br>s between its
    // rows, auto-render (which reads one text node at a time) never saw the
    // closing \], and the whole formula printed as raw TeX.
    const mathBlocks = [];
    html = html.replace(/\$\$([\s\S]*?)\$\$|(?<!\\)\\\[([\s\S]*?)\\\]/g, (match) => {
        mathBlocks.push(match);
        return `〔MATHBLOCK${mathBlocks.length - 1}〕`;
    });
    const inlineMath = [];
    html = html.replace(/\$(\S(?:[^$]*?\S)?)\$|(?<!\\)\\\(([\s\S]*?)\\\)/g, (match) => {
        inlineMath.push(match);
        return `〔INLINEMATH${inlineMath.length - 1}〕`;
    });

    // Normalize escaped line breaks (done AFTER math is stashed, so \n, \nabla,
    // \neq inside equations survive).
    html = html.replace(/\\n/g, '\n');

    // Escape HTML special chars in the prose so stray < / > / & don't get
    // parsed as tags when we innerHTML the result.
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Markdown tables first.
    html = processMarkdownTables(html);
    // Lists before the emphasis passes, so a «* » bullet is never read as a star.
    if (opts.lists) html = processMarkdownLists(html);

    // Images. Match ![alt](url). Figures are central to geometry problems, so
    // give every image a meaningful Ukrainian default alt when the source had
    // none (empty or a bare filename), wrap it in a `problem-figure` class, and
    // attach an onerror fallback that swaps a broken image for visible text.
    const DEFAULT_ALT = 'Рисунок до задачі';
    // `<`, `>`, `&` are already escaped above; also escape `"` so an alt/src
    // containing a quote can't break out of its HTML attribute (no XSS hole).
    const escapeAttr = (s) => s.replace(/"/g, '&quot;');
    html = html.replace(/!\[(.*?)\]\((.*?)\)/g, (_match, alt, src) => {
        const trimmed = (alt || '').trim();
        const looksLikeFilename = /\.(png|jpe?g|gif|svg)$/i.test(trimmed);
        const finalAlt = (!trimmed || looksLikeFilename) ? DEFAULT_ALT : alt;
        const onerror = "this.onerror=null;this.outerHTML='<span class=\\'figure-unavailable\\'>Зображення недоступне</span>'";
        return `<img class="problem-figure" src="${escapeAttr(src)}" alt="${escapeAttr(finalAlt)}"${imgDimsAttr(src)} loading="lazy" decoding="async" style="max-width:100%;height:auto;margin:1rem 0;border-radius:0.5rem" onerror="${escapeAttr(onerror)}">`;
    });

    // Bold+italic: ***text*** / ___text___ (before bold/italic).
    html = html.replace(/\*\*\*([^*]+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/(?<![a-zA-Z0-9])___([^_]+?)___(?![a-zA-Z0-9])/g, '<strong><em>$1</em></strong>');

    // Bold: **text** / __text__.
    html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/(?<![a-zA-Z0-9])__([^_]+?)__(?![a-zA-Z0-9])/g, '<strong>$1</strong>');

    // Italic: *text* (skipping ** cases).
    html = html.replace(/(?<!\*)\*([^*$]+?)\*(?!\*)/g, '<em>$1</em>');
    // Italic: _text_ (must be word-bounded so LaTeX subscripts a_1 survive).
    html = html.replace(/(?<=\s|^)_([^_\s]+?)_(?=\s|[.,!?;:]|$)/g, '<em>$1</em>');

    // Headings.
    html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

    // Code blocks + inline code.
    html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Links: [text](url).
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) => {
        const safeUrl = /^(https?:|mailto:|\/|#|\.)/i.test(url.trim()) ? url.replace(/"/g, '&quot;') : '#';
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    });

    // Paragraphs + line breaks.
    html = html.replace(/\n\n+/g, '</p><p>');
    html = html.replace(/\n/g, '<br>');

    // Restore math. Escape <, >, & inside so the browser HTML parser doesn't
    // swallow the rest of the line on something like $x<y$. The browser decodes
    // the entities back to raw chars in the text node, so KaTeX's auto-render
    // reads `&lt;` as `<` when it typesets.
    const escapeMath = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    html = html.replace(/〔MATHBLOCK(\d+)〕/g, (_, i) => escapeMath(mathBlocks[+i]));
    html = html.replace(/〔INLINEMATH(\d+)〕/g, (_, i) => escapeMath(inlineMath[+i]));

    html = `<p>${html}</p>`;
    // A list is a block, not the inside of a paragraph (a <ul> in a <p> makes
    // the parser close the <p> and leave an empty one behind it).
    if (opts.lists) {
        html = html.replace(/<p>(<ul class="md-list">)/g, '$1').replace(/(<\/ul>)<\/p>/g, '$1').replace(/<p><\/p>/g, '');
    }
    return html;
}

// ── KaTeX config (single source of truth) ────────────────────────────────
// Shared by the client-side renderer (renderMath, below) AND the build-time
// featured-card SSR (tools/export/render_featured.mjs) so the same problem can
// never render two different ways. Also used by tools/export/validate_katex.mjs
// to measure corpus coverage.
//
// Maps the dataset's non-standard commands onto KaTeX equivalents; this covers
// ~99.96% of the corpus (see validate_katex.mjs). The remaining stragglers fall
// back to visible red TeX via throwOnError:false — never a blank or a crash.
export const KATEX_MACROS = {
    '\\varangle': '\\measuredangle', '\\Varangle': '\\measuredangle',
    '\\sen': '\\operatorname{sen}', '\\tg': '\\operatorname{tg}', '\\tgg': '\\operatorname{tg}',
    '\\ctg': '\\operatorname{ctg}', '\\cotg': '\\operatorname{cotg}', '\\ctgg': '\\operatorname{ctg}',
    '\\rank': '\\operatorname{rank}', '\\lcm': '\\operatorname{lcm}',
    '\\overarc': '\\overset{\\frown}{#1}', '\\overparen': '\\overset{\\frown}{#1}',
    '\\widearc': '\\overset{\\frown}{#1}', '\\textcircled': '\\bigcirc',
};

// Delimiters mirror the old MathJax config exactly (inline $…$ / \(…\); display
// $$…$$ / \[…\]). Order matters: the longer/display openers must precede the
// shorter/inline ones so `$$` isn't mis-read as two empty `$`.
export const KATEX_DELIMITERS = [
    { left: '$$', right: '$$', display: true },
    { left: '\\[', right: '\\]', display: true },
    { left: '$', right: '$', display: false },
    { left: '\\(', right: '\\)', display: false },
];

// Split markdown into alternating text/math segments. Mirrors formatLatex's
// stash regexes exactly ($$ before $; the strict inline form so stray dollar
// signs in prose don't pair up). Display-ness is deliberately dropped —
// consumers (search-result cards) render all math inline. `src` for math is
// the inner TeX without delimiters.
const MATH_SPLIT_RE = /\$\$([\s\S]*?)\$\$|\\\[([\s\S]*?)\\\]|\$(\S(?:[^$]*?\S)?)\$|\\\(([\s\S]*?)\\\)/g;
export function splitMathSegments(text) {
    const s = String(text || '');
    const segs = [];
    let last = 0, m;
    MATH_SPLIT_RE.lastIndex = 0;
    while ((m = MATH_SPLIT_RE.exec(s)) !== null) {
        if (m.index > last) segs.push({ type: 'text', src: s.slice(last, m.index) });
        segs.push({ type: 'math', src: m[1] ?? m[2] ?? m[3] ?? m[4] ?? '' });
        last = m.index + m[0].length;
    }
    if (last < s.length) segs.push({ type: 'text', src: s.slice(last) });
    return segs;
}

// Render `el`'s math with KaTeX. Self-hosted (assests/vendor/katex) — no
// third-party origin, and consistent with the landing's build-time KaTeX. Does
// NOT block first paint: the ~275 KB of KaTeX JS is lazy-loaded on the first
// renderMath() call (the landing never calls it, so cold-load skips it), then
// cached for every later call. KaTeX renders synchronously, so once the scripts
// are in there's no typeset round-trip.
const KATEX_JS = 'assests/vendor/katex/katex.min.js';
const AUTORENDER_JS = 'assests/vendor/katex/contrib/auto-render.min.js';
let katexLoad = null;

function loadKatex() {
    if (katexLoad) return katexLoad;
    if (typeof document === 'undefined') return Promise.resolve();
    const loadScript = (src) => new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.addEventListener('load', resolve);
        s.addEventListener('error', () => reject(new Error(`failed to load ${src}`)));
        document.head.appendChild(s);
    });
    // katex.min.js must define window.katex before auto-render binds to it.
    katexLoad = loadScript(KATEX_JS).then(() => loadScript(AUTORENDER_JS));
    return katexLoad;
}

// Warm the KaTeX scripts ahead of the first renderMath() call (e.g. on search
// page mount) so the first typeset pass is instant instead of waiting on the
// ~275 KB lazy load. Idempotent; failures are swallowed (renderMath has the
// same raw-TeX fallback either way).
export function preloadKatex() {
    loadKatex().catch(() => {});
}

export function renderMath(el) {
    if (!el || typeof document === 'undefined') return Promise.resolve();
    return loadKatex().then(() => {
        if (!el.isConnected || typeof window.renderMathInElement !== 'function') return;
        // Fresh macros per call: KaTeX mutates the macros object at render time
        // (it caches \gdef expansions there), so a shared reference would leak
        // state across problems.
        window.renderMathInElement(el, {
            delimiters: KATEX_DELIMITERS,
            throwOnError: false,
            strict: false,
            macros: { ...KATEX_MACROS },
        });
    }).catch(() => {});
}
